import {makeAutoObservable, runInAction, reaction} from 'mobx';
import type {MessageSummaryViewModel} from '@/../generated/src/types';
import i18next from 'i18next';
import '@/shared/localize/initi18n';
import {settingsStore} from '@/shared/stores/settingsStore';
import {GetMessageSummaryGetService} from '@/../generated/src/GetMessageSummary/get.service';

class MessageSummaryStore {
    private _data: MessageSummaryViewModel | null = null;
    private _error: Error | null = null;
    private _isLoading = false;
    private _hasAttemptedLoad = false;
    private _refreshInterval = 5 * 60000; // 5 minutes
    private _intervalId: number | null = null;

    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    observe(callback: () => void) {
        return reaction(
            () => ({
                data: this._data,
                isLoading: this._isLoading,
                error: this._error,
                unreadCount: this.unreadCount,
            }),
            () => callback(),
            {fireImmediately: true},
        );
    }

    get data(): MessageSummaryViewModel | null {
        if (!this._hasAttemptedLoad) {
            this.fetch();
        }
        return this._data ? this.translateMessages(this._data) : null;
    }

    get isLoading(): boolean {
        return this._isLoading;
    }

    get error(): Error | null {
        return this._error;
    }

    get unreadCount(): number {
        return this._data?.unreadCount ?? 0;
    }

    private translateMessages(summary: MessageSummaryViewModel): MessageSummaryViewModel {
        const translatedSummary = {...summary};

        if (translatedSummary.firstUnreadMessages) {
            translatedSummary.firstUnreadMessages = translatedSummary.firstUnreadMessages.map(
                (message) => {
                    if (!message.subject && message.subjectTranslation) {
                        const params: Record<string, string> = {};
                        if (message.subjectTranslation.translationParameters) {
                            for (const param of message.subjectTranslation.translationParameters) {
                                if (param.key && param.value) {
                                    params[param.key] = param.value;
                                }
                            }
                        }
                        return {
                            ...message,
                            subject: i18next.t(message.subjectTranslation.translationKey, params),
                        };
                    }
                    return message;
                },
            );
        }

        return translatedSummary;
    }

    startPolling() {
        this.stopPolling();
        this.fetch();
        this._intervalId = window.setInterval(() => this.fetch(), this._refreshInterval);
    }

    stopPolling() {
        if (this._intervalId !== null) {
            window.clearInterval(this._intervalId);
            this._intervalId = null;
        }
    }

    async fetch() {
        if (this._isLoading) return;

        runInAction(() => {
            this._isLoading = true;
            this._error = null;
        });

        try {
            const headers = settingsStore.headers;
            if (!headers) {
                throw new Error('No headers available');
            }

            const response = await GetMessageSummaryGetService({limit: 5}, {headers});

            runInAction(() => {
                this._data = response.data;
                this._hasAttemptedLoad = true;
                this._isLoading = false;
            });
        } catch (error) {
            runInAction(() => {
                this._error = error instanceof Error ? error : new Error('Unknown error occurred');
                this._isLoading = false;
                this._hasAttemptedLoad = true;
            });
        }
    }

    reset() {
        runInAction(() => {
            this._data = null;
            this._error = null;
            this._isLoading = false;
            this._hasAttemptedLoad = false;
        });
        this.stopPolling();
    }
}

export const messageSummaryStore = new MessageSummaryStore();
